import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';

export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  filled?: boolean;
  color?: string;
  semanticId?: string;
}

const sizeToPx: Record<NonNullable<IconProps['size']>, number> = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

const StyledIcon = styled.span.withConfig({
  shouldForwardProp: (prop) =>
    !['$sizePx', '$filled', '$color'].includes(String(prop)),
})<{
  $sizePx: number;
  $filled: boolean;
  $color?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $sizePx }) => `${$sizePx}px`};
  line-height: 1;
  width: 1em;
  height: 1em;
  color: ${({ $color }) => $color ?? 'currentColor'};
  font-variation-settings: ${({ $filled }) =>
    $filled
      ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
      : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"} !important;
  user-select: none;
`;

export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(function Icon(
  {
    name,
    size = 'md',
    filled = false,
    color,
    semanticId,
    'aria-hidden': ariaHidden = true,
    className,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <StyledIcon
      ref={ref}
      className={['material-symbols-outlined', className].filter(Boolean).join(' ')}
      $sizePx={sizeToPx[size]}
      $filled={filled}
      $color={color}
      aria-hidden={ariaHidden}
      {...semanticProps}
      {...rest}
    >
      {name}
    </StyledIcon>
  );
});

Icon.displayName = 'Icon';
